import { FC, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { InfoTableDto, InfoTableRow } from 'src/apis/direct-fcfs/types';
import { Card } from 'src/components/data-display/card';
import { Table } from 'src/components/data-display/smart-table';
import { Td, Th } from 'src/components/data-display/table';
import { Heading } from 'src/components/text/heading';
import { isServerError, useAxios } from 'src/hooks/use-axios';
import { Spacer } from 'src/pages/settings/registration/components/form-utils';

export function useDirectFcfsInfoTable(eic?: string) {
  const infoTable = useAxios(
    (axios, signal, eic: string) =>
      axios.request<InfoTableDto>({
        signal,
        url: `${PRISMA_CONFIG.directFcfs}/info-table/${eic}`,
        method: 'GET',
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        if (isServerError(error, 404) || isServerError(error, 401)) {
          // this is OK
          return error;
        } else {
          throw error;
        }
      },
    }
  );

  useEffect(() => {
    if (!(PRISMA_CONFIG.directFcfs && eic)) return;
    infoTable.execute(eic);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eic]);

  if (!PRISMA_CONFIG.directFcfs) return;
  if (!eic) return;

  return infoTable;
}

export type DirectFcfsInfoTableRequest = ReturnType<
  typeof useDirectFcfsInfoTable
>;

export const DirectFcFsTable: FC<{
  infoTableRequest: DirectFcfsInfoTableRequest;
}> = ({ infoTableRequest }) => {
  const data = infoTableRequest?.response?.data;

  if (!data) return null;

  const showMultiplier = data.rows?.some((row) => !!row.multiplier) ?? false;
  const showMinimum = data.rows?.some((row) => !!row.minimumQuantity) ?? false;

  return (
    <Card>
      <Heading mode="card">Booking Direct FCFS</Heading>

      <Spacer />

      <Table
        data={data.rows ?? []}
        data-testid="direct-fcfs-info-table"
        setId={(_, idx) => idx}
        cols={[
          {
            key: 'product',
            head: <Th>Product</Th>,
            body: (row) => (
              <Td>
                <ReactMarkdown>{row.product ?? ''}</ReactMarkdown>
              </Td>
            ),
          },
          {
            key: 'runtime',
            head: <Th>Runtime</Th>,
            body: (row) => (
              <Td>
                <ReactMarkdown>{row.runtime ?? ''}</ReactMarkdown>
              </Td>
            ),
          },
          ...(showMultiplier
            ? [
                {
                  key: 'multiplier',
                  head: <Th>Multiplier</Th>,
                  body: (row: InfoTableRow) => (
                    <Td>
                      <ReactMarkdown>{row.multiplier ?? ''}</ReactMarkdown>
                    </Td>
                  ),
                },
              ]
            : []),
          ...(showMinimum
            ? [
                {
                  key: 'minimumQuantity',
                  head: <Th>Minimum quantity</Th>,
                  body: (row: InfoTableRow) => (
                    <Td>
                      <ReactMarkdown>{row.minimumQuantity ?? ''}</ReactMarkdown>
                    </Td>
                  ),
                },
              ]
            : []),
          {
            key: 'bookingDeadline',
            head: <Th>Booking deadline</Th>,
            body: (row) => (
              <Td>
                <ReactMarkdown>{row.bookingDeadline ?? ''}</ReactMarkdown>
              </Td>
            ),
          },
        ]}
        hint={
          <ul>
            {data.comments?.map((comment, idx) => <li key={idx}>{comment}</li>)}
          </ul>
        }
      />
    </Card>
  );
};
